import React, { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import {
  useChatConversationAddMembers,
  useChatSystemConversationAddMembers,
  useChatUsers,
  useSearchChatUsers,
} from "~/queries";
import { Icon, LoadingIcon } from "~/ui/components/icons";
import { TextInput } from "~/ui/components/inputs";
import { useChatAuthAccessToken } from "~/ui/context";

import { ChatUsersRenderer } from "../../../chatUsersRenderer/ChatUsersRenderer";

const limit = 15;

export const SearchMembers: FC<{
  chatId: number;
  shouldUseSystem: boolean;
}> = ({ chatId, shouldUseSystem = false }) => {
  const accessToken = useChatAuthAccessToken();
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [memberToAdd, setMemberToAdd] = useState<number | string | null>(null);
  const { data: chatSearchUsers, isLoading } = useSearchChatUsers(
    searchTerm,
    limit
  );
  const { data: chatUsers, isLoading: isChatUsersLoading } = useChatUsers(
    limit
  );

  const {
    mutateAsync: addMember,
    isLoading: isAddMemberLoading,
  } = useChatConversationAddMembers();
  const {
    mutateAsync: addMemberSystem,
    isLoading: isAddMemberLoadingSystem,
  } = useChatSystemConversationAddMembers();

  const handleMemberAdd = (id: string | number | undefined) => {
    if (!id) return;
    setMemberToAdd(id);
    if (shouldUseSystem) {
      addMemberSystem([chatId, [Number(id)]]).then(() => setMemberToAdd(null));
    } else {
      addMember([chatId, accessToken, [Number(id)]]).then(() =>
        setMemberToAdd(null)
      );
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(search);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  return (
    <div className="h-full flex flex-col">
      <TextInput
        value={search}
        onChange={handleSearchChange}
        placeholder={intl.formatMessage({
          defaultMessage: "Search users",
          id: "JjGgXI",
        })}
        boxSize="lg"
        className="bg-dark"
        wrapperClassname="mg-sm bg-dark"
      />
      {search === "" ? (
        <div>
          {isChatUsersLoading ? (
            <LoadingIcon className="mg-0-auto mg-t-md mg-b-md flex justify-center" />
          ) : (
            <ChatUsersRenderer
              wrapperClassname="h-max-200px"
              users={chatUsers?.data ?? []}
              onClick={handleMemberAdd}
              useDisplayName={false}
              isLoading={isLoading}
              isActionLoading={isAddMemberLoading || isAddMemberLoadingSystem}
              actionItemId={memberToAdd}
              useId
              icon={
                <Icon
                  icon={["far", "user-plus"]}
                  size="lg"
                  className="pd-r-sm pointer"
                />
              }
            />
          )}
        </div>
      ) : (
        <div>
          {isLoading ? (
            <LoadingIcon className="mg-0-auto mg-t-md mg-b-md flex justify-center" />
          ) : (
            <div className="pd-b-md">
              <ChatUsersRenderer
                wrapperClassname="h-max-300px"
                users={chatSearchUsers?.data ?? []}
                onClick={handleMemberAdd}
                useDisplayName
                isLoading={isLoading}
                isActionLoading={isAddMemberLoading || isAddMemberLoadingSystem}
                actionItemId={memberToAdd}
                useId
                icon={
                  <Icon
                    icon={["far", "user-plus"]}
                    size="lg"
                    className="pd-r-sm pointer"
                  />
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
